import { createCollection } from '@rocketlane/state-db';
import { taskCollection } from 'collections/tasks';
import { converToEntityId, PhaseEntityType } from 'data/projects/phase-entity';
/** Stable serialization of the plan filters for lane identity. */ export const getPlanFiltersHash = (filters)=>{
    var _ref, _ref1, _ref2, _ref3;
    return JSON.stringify([
        (_ref = filters === null || filters === void 0 ? void 0 : filters.completed) !== null && _ref !== void 0 ? _ref : null,
        (_ref1 = filters === null || filters === void 0 ? void 0 : filters.responsibleCompanyIds) !== null && _ref1 !== void 0 ? _ref1 : [],
        (_ref2 = filters === null || filters === void 0 ? void 0 : filters.includePrivate) !== null && _ref2 !== void 0 ? _ref2 : true,
        (_ref3 = filters === null || filters === void 0 ? void 0 : filters.budgetIds) !== null && _ref3 !== void 0 ? _ref3 : []
    ]);
};
export const getPlanLaneKey = (projectId, phaseId, filters)=>`${projectId}:${phaseId}:${getPlanFiltersHash(filters)}`;
/**
 * Same key, from an already-hashed lane rather than raw filters. Used when
 * moving between two lanes that must share a filter set — the hash is the only
 * form the source lane still has.
 */ export const getPlanLaneKeyFromHash = (projectId, phaseId, filtersHash)=>`${projectId}:${phaseId}:${filtersHash}`;
export const planLaneCollection = createCollection({
    id: 'plan-lanes',
    getKey: (lane)=>lane.laneKey,
    indexes: {
        byProject: (lane)=>lane.projectId,
        byPhase: (lane)=>lane.projectPhaseId
    }
});
export const planBoardPhaseCollection = createCollection({
    id: 'plan-board-phases',
    getKey: (entry)=>entry.projectPhaseId,
    indexes: {
        byProject: (entry)=>entry.projectId
    }
});
/**
 * Strip tokens for tasks that no longer exist (delete, WS externalDelete) —
 * one central fixup instead of per-call-site cleanup. Extra safety net only:
 * rendering already skips tokens with no matching entity, and membership truth
 * always comes back via lane refetch.
 */ const reconcileDeadTokens = (changedIds)=>{
    const deadTokens = new Set();
    changedIds.forEach((id)=>{
        if (taskCollection.get(id) === undefined && typeof id === 'number') {
            deadTokens.add(converToEntityId(id, PhaseEntityType.Task));
        }
    });
    if (deadTokens.size === 0) {
        return;
    }
    const lanesToPatch = planLaneCollection.getAll().filter((lane)=>lane.pageTokens.some((token)=>deadTokens.has(token)));
    if (lanesToPatch.length === 0) {
        return;
    }
    planLaneCollection.push(lanesToPatch.map((lane)=>({
            ...lane,
            pageTokens: lane.pageTokens.filter((token)=>!deadTokens.has(token))
        })), {
        source: 'local'
    });
};
/**
 * Re-file a task's token when its phase changes.
 *
 * The board renders each phase from its lane's `pageTokens`, and until this
 * existed nothing moved a token between lanes. Dragging a card worked because
 * `movePlanEntityTokensOptimistic` patched the lanes by hand; moving the same
 * task from the task drawer did not, and the only remaining mechanism was a
 * lane refetch — `fetchLaneFirstPage`, which reads page ONE. A task that lands
 * past the first page of its destination is invisible until the lane is
 * scrolled, so the card simply stayed in the phase it left.
 *
 * Deriving it from the task entity instead fixes both tabs with one mechanism.
 * The tab that made the move sees it the instant the optimistic overlay
 * applies; a peer sees it when the entity arrives over the cross-tab channel.
 * Neither waits on a request, and there is no page boundary to fall past.
 *
 * Written with `source: 'local'`: every tab derives this from the same synced
 * task, so broadcasting the result would have peers apply it twice — once from
 * their own reconciler and once from the message. Same reason the phase
 * progress counters stay local.
 */ const reconcilePhaseTokens = (changedIds)=>{
    if (planLaneCollection.size() === 0) {
        return;
    }
    // Accumulated so several tasks moving at once (a bulk edit) fold into one
    // write per lane rather than one per task.
    const patched = new Map();
    const laneOf = (key)=>{
        var _patched_get;
        return (_patched_get = patched.get(key)) !== null && _patched_get !== void 0 ? _patched_get : planLaneCollection.get(key);
    };
    changedIds.forEach((id)=>{
        var _task_projectPhase, _task_project;
        if (typeof id !== 'number') {
            return;
        }
        const task = taskCollection.get(id);
        const phaseId = task === null || task === void 0 ? void 0 : (_task_projectPhase = task.projectPhase) === null || _task_projectPhase === void 0 ? void 0 : _task_projectPhase.projectPhaseId;
        const projectId = task === null || task === void 0 ? void 0 : (_task_project = task.project) === null || _task_project === void 0 ? void 0 : _task_project.projectId;
        if (phaseId == null || projectId == null) {
            return;
        }
        const token = converToEntityId(id, PhaseEntityType.Task);
        planLaneCollection.getByIndex('byProject', projectId).forEach(({ laneKey, projectPhaseId })=>{
            const stale = laneOf(laneKey);
            if (stale === undefined || projectPhaseId === phaseId || !stale.pageTokens.includes(token)) {
                return;
            }
            patched.set(laneKey, {
                ...stale,
                pageTokens: stale.pageTokens.filter((t)=>t !== token),
                totalCount: Math.max(0, stale.totalCount - 1),
                filteredCount: Math.max(0, stale.filteredCount - 1)
            });
            // The destination lane under the SAME filters. Matching the hash
            // matters: a task visible under one filter set in its old phase
            // belongs in that same filter set's lane for its new phase, and in no
            // other. A lane this tab has not loaded is simply skipped — there is
            // no window for the token to be part of.
            const destKey = getPlanLaneKeyFromHash(projectId, phaseId, stale.filtersHash);
            const dest = laneOf(destKey);
            if (dest === undefined || dest.pageTokens.includes(token)) {
                return;
            }
            // Appended, not positioned: a drawer move carries no anchor. On a
            // fully-loaded lane that is exactly where the server puts it; on a
            // partial window it is approximate, and the lane's next fetch settles
            // the real order.
            patched.set(destKey, {
                ...dest,
                pageTokens: [
                    ...dest.pageTokens,
                    token
                ],
                totalCount: dest.totalCount + 1,
                filteredCount: dest.filteredCount + 1
            });
        });
    });
    if (patched.size > 0) {
        planLaneCollection.push([
            ...patched.values()
        ], {
            source: 'local'
        });
    }
};
let reconcilerAttached = false;
/**
 * Keep lane token windows coherent with `taskCollection`, for the two changes
 * that invalidate them: a task disappearing, and a task changing phase.
 */ export const attachPlanLaneReconciler = ()=>{
    if (reconcilerAttached) {
        return;
    }
    reconcilerAttached = true;
    taskCollection.subscribeToChanges((changedIds)=>{
        reconcileDeadTokens(changedIds);
        reconcilePhaseTokens(changedIds);
    });
};
